import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface I9Record {
  id: string;
  company_id: string;
  vendor_id: string;
  worker_id: string | null;
  legal_first_name: string;
  legal_middle_name: string | null;
  legal_last_name: string;
  other_last_names: string | null;
  date_of_birth: string;
  ssn_last4: string | null;
  ssn_encrypted: string | null;
  citizenship_status: string;
  alien_registration_number: string | null;
  i94_number: string | null;
  foreign_passport_number: string | null;
  foreign_passport_country: string | null;
  work_auth_expiry_date: string | null;
  section1_signed_by: string | null;
  section1_signed_at: string | null;
  document_list_type: string | null;
  list_a_document_type: string | null;
  list_a_document_number: string | null;
  list_a_issuing_authority: string | null;
  list_a_expiry_date: string | null;
  list_a_document_path: string | null;
  list_b_document_type: string | null;
  list_b_document_number: string | null;
  list_b_issuing_state: string | null;
  list_b_expiry_date: string | null;
  list_b_document_path: string | null;
  list_c_document_type: string | null;
  list_c_document_number: string | null;
  list_c_issuing_authority: string | null;
  list_c_document_path: string | null;
  first_day_of_work: string | null;
  section2_verified_by_name: string | null;
  section2_verified_at: string | null;
  is_manual_import: boolean;
  manual_import_source: string | null;
  manual_import_notes: string | null;
  i9_status: string;
  created_at: string;
  updated_at: string;
}

export interface EVerifyCase {
  id: string;
  company_id: string;
  vendor_id: string;
  i9_record_id: string;
  case_number: string | null;
  case_status: string;
  submitted_at: string | null;
  result_received_at: string | null;
  date_of_hire: string;
  is_manual_case: boolean;
  manual_case_notes: string | null;
  created_at: string;
}

export const useWorkerI9 = (vendorId?: string) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["worker_i9", vendorId],
    queryFn: async () => {
      const { data, error } = await (supabase
        .from("worker_i9_records") as any)
        .select("*")
        .eq("vendor_id", vendorId!)
        .maybeSingle();
      if (error) throw error;
      return data as I9Record | null;
    },
    enabled: !!user && !!vendorId,
  });
};

export const useEVerifyCases = (vendorId?: string) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["everify_cases", vendorId],
    queryFn: async () => {
      const { data, error } = await (supabase
        .from("worker_everify_cases") as any)
        .select("*")
        .eq("vendor_id", vendorId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as EVerifyCase[];
    },
    enabled: !!user && !!vendorId,
  });
};

export const useCreateI9Record = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Record<string, any>) => {
      const { data, error } = await (supabase
        .from("worker_i9_records") as any)
        .insert(input)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["worker_i9", vars.vendor_id] });
      qc.invalidateQueries({ queryKey: ["vendor_compliance_status"] });
    },
  });
};

export const useCreateEVerifyCase = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Record<string, any>) => {
      const { data, error } = await (supabase
        .from("worker_everify_cases") as any)
        .insert(input)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["everify_cases", vars.vendor_id] });
    },
  });
};
